import {
    MainScore,
    ScoreValue,
    Dimension,
    DimensionValue,
    NumericalScoreValue,
    JsonScoreValue,
} from "src/api/client-api/olapScores/olapScoresSchema";
import { getDisplayValueForScore } from "src/utils/scores";

export type FlattenedDimensionValue = {
    id: string;
    score: ScoreValue;
    label: string;
};

export class OlapScoresService {
    public static getDimension(name: string | undefined, dimensions: Dimension[] | undefined): Dimension | undefined {
        return dimensions?.find((dimension: Dimension) => dimension.label === name) ?? undefined;
    }

    public static getDimensionValue(
        key: string | number | undefined,
        dimension: Dimension | undefined,
    ): DimensionValue | undefined {
        return dimension?.values.find((value: DimensionValue) => value.id === key?.toString()) ?? undefined;
    }

    public static getPropertyDimensionValue(
        propertyId: number,
        ssqVersionStructureId: number | null | undefined,
        dimension: Dimension | undefined,
    ): DimensionValue | undefined {
        if (ssqVersionStructureId != null) {
            const occurrenceValue = OlapScoresService.getDimensionValue(
                `${propertyId}_${ssqVersionStructureId}`,
                dimension,
            );
            if (occurrenceValue !== undefined) return occurrenceValue;
        }

        return OlapScoresService.getDimensionValue(propertyId, dimension);
    }

    public static getDimensionValueDimensions(
        mainScore: MainScore | undefined,
        name: string | undefined,
        key: string | number | undefined,
    ): Dimension[] | undefined {
        if (name === undefined || key === undefined) return mainScore?.dimensions;

        const dimension = OlapScoresService.getDimension(name, mainScore?.dimensions);
        const dimensionValue = OlapScoresService.getDimensionValue(key, dimension);

        return dimensionValue?.dimensions;
    }

    public static findDimension(name: string | undefined, dimensions: Dimension[] | undefined): Dimension | undefined {
        if (!dimensions) return undefined;

        for (const dimension of dimensions) {
            if (dimension.label === name) return dimension;

            const dimensionValues = dimension.values;

            for (const dimensionValue of dimensionValues) {
                const dimension = OlapScoresService.getDimension(name, dimensionValue.dimensions);

                if (dimension) return dimension;

                const firstDimension = OlapScoresService.findDimension(name, dimensionValue.dimensions);

                if (firstDimension) return firstDimension;
            }
        }

        return undefined;
    }

    public static findDimensionValueWithDimension(
        name: string | undefined,
        dimensions: Dimension[] | undefined,
    ): DimensionValue | undefined {
        if (!dimensions) return undefined;

        for (const dimension of dimensions) {
            const dimensionValues = dimension.values;

            for (const dimensionValue of dimensionValues) {
                const dimension = OlapScoresService.getDimension(name, dimensionValue.dimensions);

                if (dimension) return dimensionValue;

                const firstDimensionValue = OlapScoresService.findDimensionValueWithDimension(
                    name,
                    dimensionValue.dimensions,
                );

                if (firstDimensionValue) return firstDimensionValue;
            }
        }

        return undefined;
    }

    public static getSubscoreWithType(type: string, mainScore: MainScore | undefined): MainScore | undefined {
        if (mainScore === undefined) return undefined;

        return Object.values(mainScore.subscores).find((subscore: MainScore) => subscore.type === type) ?? undefined;
    }

    public static getSubscoreWithDisplayMode(
        displayMode: string,
        mainScore: MainScore | undefined,
    ): MainScore | undefined {
        if (mainScore === undefined) return undefined;

        return (
            Object.values(mainScore.subscores).find((subscore: MainScore) => subscore.displayMode === displayMode) ??
            undefined
        );
    }

    public static flattenDimensionValues = (
        dimension: Dimension | undefined,
        excludeEmptyScores: boolean = true,
    ): FlattenedDimensionValue[] => {
        return (
            dimension?.values.flatMap((dimensionValue: DimensionValue) => {
                const id = dimensionValue.id;
                const label = dimensionValue.label;
                const score = dimensionValue.score.value;

                if (excludeEmptyScores && score === null) {
                    return [];
                }

                return [{ id, label, score }];
            }) ?? []
        );
    };

    public static flattenDimensionValuesPerDimension = (
        dimensions: Dimension[] | undefined,
    ): Record<string, DimensionValue[]> | undefined => {
        return dimensions?.reduce((acc, dimension: Dimension) => {
            return {
                ...acc,
                [dimension.label]: dimension.values,
            };
        }, {});
    };

    public static normalizeNumericalScore(
        value: ScoreValue | undefined,
        fallback: number,
    ): NonNullable<NumericalScoreValue>;
    public static normalizeNumericalScore(value: ScoreValue | undefined, fallback?: null): NumericalScoreValue;
    public static normalizeNumericalScore(
        value: ScoreValue | undefined,
        fallback: number | null = null,
    ): NumericalScoreValue {
        return typeof value === "number" && Number.isFinite(value) ? value : fallback;
    }

    public static normalizeJsonScore = (value: ScoreValue | undefined): JsonScoreValue => {
        return typeof value === "object" ? value : null;
    };

    public static formatScoreDimensions = (
        scoreDimension: {
            value?: DimensionValue;
            grade?: DimensionValue;
        },
        precision: number,
    ): string | null => {
        const scoreValue = this.normalizeNumericalScore(scoreDimension.value?.score.value);
        const gradeValue = this.normalizeNumericalScore(scoreDimension.grade?.score.value);

        if (scoreValue === null) return null;

        const score = getDisplayValueForScore(scoreValue, precision);
        if (gradeValue !== null) {
            const grade = getDisplayValueForScore(gradeValue, precision);
            return `${score} / ${grade}`;
        }

        return `${score}`;
    };
}
